// File export (IMPLEMENTATION §2.9 "Export order"): `navigator.share({files})` in a secure context →
// `<a download>` → a copy textarea. M12's DebugPanel and M7's Settings "Export telemetry" use it; the dev-only
// "Send to PC" posts to the Vite dev plugin (`POST /__gm/upload`, tools/vite-plugin-gm-dev.ts).
// Environment-injected (`ExportEnv`) so the order is unit-tested without a browser.

export type ExportMethod = 'share' | 'download' | 'copy' | 'cancelled';

export interface ExportFile {
  name: string;
  /** MIME type, e.g. text/csv or application/json. */
  type: string;
  text: string;
}

/** The browser surface the exporter uses (window/navigator/document in the app, fakes in tests). */
export interface ExportEnv {
  isSecureContext: boolean;
  navigator: {
    share?: (data: { files: File[]; title?: string }) => Promise<void>;
    canShare?: (data: { files: File[] }) => boolean;
  };
  /** Builds a File (absent: no File constructor → no share). */
  makeFile?: (f: ExportFile) => File;
  /** Starts an `<a download>` save; false when the engine has no download attribute support. */
  download(f: ExportFile): boolean;
  /** Shows the text in a selectable textarea (last resort). */
  showCopy(f: ExportFile): void;
}

/** Tries share, then download, then the copy textarea. A user-cancelled share is final (`cancelled`). */
export async function exportFile(env: ExportEnv, f: ExportFile): Promise<ExportMethod> {
  const nav = env.navigator;
  if (env.isSecureContext && env.makeFile && typeof nav.share === 'function') {
    const file = env.makeFile(f);
    const data = { files: [file], title: f.name };
    if (typeof nav.canShare !== 'function' || nav.canShare({ files: [file] })) {
      try {
        await nav.share(data);
        return 'share';
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') return 'cancelled';
        // NotAllowedError (no user activation), TypeError, …: fall through to the download
      }
    }
  }
  if (env.download(f)) return 'download';
  env.showCopy(f);
  return 'copy';
}

/** The real browser environment. `showCopy` is the caller's UI (the panel's textarea). */
export function browserExportEnv(
  win: Window & typeof globalThis,
  showCopy: (f: ExportFile) => void,
): ExportEnv {
  const nav = win.navigator as Navigator & ExportEnv['navigator'];
  // the previous download's object URL is revoked at the next one (Safari may still be reading it right
  // after the click)
  let lastUrl: string | null = null;
  return {
    isSecureContext: win.isSecureContext,
    navigator: {
      ...(typeof nav.share === 'function' ? { share: (d) => nav.share(d) } : {}),
      ...(typeof nav.canShare === 'function' ? { canShare: (d) => nav.canShare(d) } : {}),
    },
    ...(typeof win.File === 'function'
      ? { makeFile: (f: ExportFile) => new win.File([f.text], f.name, { type: f.type }) }
      : {}),
    download(f) {
      const doc = win.document;
      const a = doc.createElement('a');
      if (!('download' in a)) return false;
      if (lastUrl !== null) win.URL.revokeObjectURL(lastUrl);
      const url = win.URL.createObjectURL(new win.Blob([f.text], { type: f.type }));
      lastUrl = url;
      a.href = url;
      a.download = f.name;
      a.rel = 'noopener';
      a.style.display = 'none';
      doc.body.appendChild(a);
      a.click();
      a.remove();
      return true;
    },
    showCopy,
  };
}

/** Dev only: "Send to PC" — POST the text to the Vite dev server (`web/out/<kind>/<name>`). */
export async function sendToPc(
  fetchFn: typeof fetch,
  kind: string,
  f: ExportFile,
): Promise<{ ok: boolean; file?: string; error?: string }> {
  try {
    const q = new URLSearchParams({ kind, name: f.name });
    const res = await fetchFn(`/__gm/upload?${q.toString()}`, {
      method: 'POST',
      headers: { 'content-type': f.type },
      body: f.text,
    });
    const body = (await res.json().catch(() => ({}))) as {
      ok?: boolean;
      file?: string;
      error?: string;
    };
    return res.ok && body.ok === true
      ? { ok: true, ...(body.file ? { file: body.file } : {}) }
      : { ok: false, error: body.error ?? `HTTP ${res.status}` };
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : String(err) };
  }
}

/** File-name stamp: 20260929-143015 (local time). */
export function fileStamp(d: Date): string {
  const p = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}-${p(d.getHours())}${p(d.getMinutes())}${p(d.getSeconds())}`;
}
